/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function validateRepField() {
  const repWrapper = document.querySelector('.cart-sales-rep');
  const select = document.querySelector('select[name="attributes[Sales Representative]"]');
  const repValue = select?.value.trim() || '';
  const dynamicBtns = document.querySelector('.dynamic-cart-btns');
  const cartCheckoutBtn = document.querySelector('button[name="checkout"]');
  const drawerBtns = document.querySelector('.cart-drawer__checkout-buttons');
  const isValid = repValue !== '';
  const cartItems = document.querySelectorAll('.cart-item'); // adjust selector if needed
  
  // If cart is empty, remove the Sales Rep field
  if (repWrapper && cartItems.length === 0) {
    repWrapper.style.display = 'none';
    return; 
  }

  if (dynamicBtns) {
    dynamicBtns.classList.toggle('disabled', !isValid);
  }

  if (cartCheckoutBtn) {
    cartCheckoutBtn.dataset.disabled = isValid ? 'false' : 'true';
    cartCheckoutBtn.style.opacity = isValid ? '1' : '0.6';
    cartCheckoutBtn.style.cursor = isValid ? 'pointer' : 'not-allowed';
  }

  if (drawerBtns) {
    drawerBtns.dataset.disabled = isValid ? 'false' : 'true';
    drawerBtns.style.opacity = isValid ? '1' : '0.7';
    drawerBtns.style.cursor = isValid ? 'pointer' : 'not-allowed';
  }

  if (select && isValid) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Sales Representative': repValue
        }
      })
    });
    
   select.classList.remove('sales-rep-error');

    const firstOption = select.querySelector('option[value=""]');
    if (firstOption) firstOption.disabled = true;
  }
}

// Bind change event
document.addEventListener('change', function (e) {
  if (e.target.matches('select[name="attributes[Sales Representative]"]')) {
    validateRepField();
  }
});
// on load
document.addEventListener('DOMContentLoaded', validateRepField);

// Observe cart updates
const observer = new MutationObserver(validateRepField);
observer.observe(document.body, { childList: true, subtree: true });

// Show alert if user clicks disabled checkout btns
document.addEventListener('click', function (e) {
  const dynamicBtns = document.querySelector('.dynamic-cart-btns');
  const cartCheckoutBtn = document.querySelector('button[name="checkout"]');
  const drawerBtns = document.querySelector('.cart-drawer__checkout-buttons');
  const select = document.querySelector('select[name="attributes[Sales Representative]"]');
  const drawerContent = document.querySelector('.drawer__content');

  if ( (dynamicBtns && dynamicBtns.classList.contains('disabled') && dynamicBtns.contains(e.target)) ||
    (drawerBtns && drawerBtns.contains(e.target) && drawerBtns.dataset.disabled === 'true') ||
    (cartCheckoutBtn && e.target === cartCheckoutBtn && cartCheckoutBtn.dataset.disabled === 'true') ) {
    
        e.preventDefault();
        if (select) {
          select.classList.add('sales-rep-error');
        }
        if (drawerContent && select) {
          const offsetTop = select.offsetTop;
          drawerContent.scrollTo({
            top: offsetTop - 20, // small offset for padding
            behavior: 'smooth'
          });
        }
        alert('Please let us know who helped with your order');
  }
});




function getLeadEstimateText(leadDate, leadTime) {
  // Default case
  if (!leadTime) {
    return "Estimated shipping in 2–4 weeks";
  }

  // Special case: Lead Time 99 = manual handling
  if (leadTime === 99) {
    return "Call your Sales Representative for Availability";
  }

  // If leadDate is missing → show full Lead Time
  if (!leadDate) {
    return `Estimated shipping in ${leadTime} weeks`;
  }

  const now = new Date();
  const restockDate = new Date(leadDate);
  const msElapsed = now - restockDate; // Time passed since out of stock
  const daysElapsed = Math.floor(msElapsed / (1000 * 60 * 60 * 24));
  const weeksElapsed = daysElapsed / 7;

  let weeksRemaining = leadTime - weeksElapsed;
  console.log(leadTime , weeksElapsed);

  if (weeksRemaining <= 0) {
    return "Shipping Soon";
  }
  else if (weeksRemaining <= 1) {
    return "Shipping in 1 week or less";
  }

  weeksRemaining = Math.ceil(weeksRemaining);
  console.log(weeksRemaining);
  return `Estimated shipping in ${weeksRemaining} weeks`;
}

function renderLeadEstimate(container, updateInventoryText = false) {
  const output = container.querySelector('.lead-estimate__text');
  const inventoryBar = container.parentElement?.querySelector('.product-inventory, .product-inventory__status');
  const leadDate = container.dataset.soldoutDate;
  const leadTime = parseFloat(container.dataset.restockTime || "0");
  const variantStock = parseInt(container.dataset.variantStock || "0");
  const inventoryTracked = container.dataset.inventoryTracked === "true";

  if (!output) return;

  if (inventoryTracked && variantStock < 1) {
    // Out of stock case: show lead estimate
    const estimate = getLeadEstimateText(leadDate, leadTime);
    output.textContent = estimate;

    if (inventoryBar) inventoryBar.style.display = 'none';
  } else {
    // In stock case
    output.textContent = "";

    if (inventoryBar) {
      inventoryBar.style.display = inventoryTracked ? 'block' : 'none';

      if (updateInventoryText && inventoryTracked && variantStock > 0) {
        // Update inventory status text ONLY after user clicks variant
        if (variantStock <= 2) {
          inventoryBar.textContent = variantStock === 1
            ? `Very low stock (1 unit)`
            : `Very low stock (${variantStock} units)`;
          inventoryBar.setAttribute('data-inventory-level', 'very_low');
        } else if (variantStock <= 10) {
          inventoryBar.textContent = `Low stock (${variantStock} units)`;
          inventoryBar.setAttribute('data-inventory-level', 'low');
        } else {
          inventoryBar.textContent = `In stock (${variantStock} units)`;
          inventoryBar.setAttribute('data-inventory-level', 'normal');
        }
      }
    }
  }
}


function initLeadEstimates() {
  document.querySelectorAll('.lead-estimate').forEach(container => {
    renderLeadEstimate(container, true);
    console.log(container.textContent.replace(/\s+/g, ' ').trim());

    const stockatAtc = document.getElementById('stock-at-atc');
    if(stockatAtc.value < 1)
      stockatAtc.value = container.textContent;
  });
}

document.addEventListener('DOMContentLoaded', initLeadEstimates);

// Handle Load More on collection cards
document.addEventListener('products:loaded', function() {
  initLeadEstimates();
});

// Handle variant change on product page (full page, not collection cards)
document.addEventListener('on:variant:change', (event) => {
  const variant = event.detail.variant;
  if (!variant) return;
  
  //update value for hidden input element on variant change
  const stockatAtc = document.getElementById('stock-at-atc');
  stockatAtc.value = variant.inventory_quantity;

  const productCard = event.target.closest('.product-card, .product, .product-wrapper');
  const container = productCard?.querySelector('.lead-estimate');
  if (!container) return;

  container.setAttribute('data-variant-stock', variant.inventory_quantity);
  container.setAttribute('data-inventory-tracked', variant.inventory_management ? 'true' : 'false');

  renderLeadEstimate(container, false);

  //update value for hidden input element if out of stock
  if(variant.inventory_quantity < 1)
    stockatAtc.value = container.textContent;
});

// Handle swatch click in collection grid (because on:variant:change does not fire)
document.addEventListener('change', (event) => {
  if (event.target.matches('.opt-btn.js-option')) {
    console.log("changed");
    const swatchInput = event.target;

    const productCard = swatchInput.closest('.card--product, .grid__item, .product-item');
    if (!productCard) return;

    const container = productCard.querySelector('.lead-estimate');
    if (!container) return;

    const select = productCard.querySelector('select[name^="variant-data-"]');
    if (!select) return;

    const selectedVariantId = swatchInput.dataset.variantId;
    console.log(selectedVariantId);
    const matchingOption = select.querySelector(`option[value="${selectedVariantId}"]`);
    if (!matchingOption) return;

    const variantStock = matchingOption.dataset.inventoryQuantity;
    const inventoryTracked = matchingOption.dataset.inventoryManagement === "true" ? 'true' : 'false';

    container.setAttribute('data-variant-stock', variantStock || "0");
    container.setAttribute('data-inventory-tracked', inventoryTracked);

    renderLeadEstimate(container, true);
  }
});


